// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseAddressModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import CustomerCard, { CustomerCardStyles } from "@insite/content-library/Components/CustomerCard";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll from "@vanmeter/App/Components/Common/FancyScroll";
import React, { useState } from "react";

interface OwnProps {
    customers: BaseAddressModel[];
    pagination: PaginationModel | null;
    selectedCustomer?: BaseAddressModel;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    onChangePage: (page: number) => void;
    onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    extendedStyles?: CustomerSelectorStyles;
    isOverlay?: boolean;
}

export interface CustomerSelectorStyles {
    customerCard?: CustomerCardStyles;
    customerCardSelected?: CustomerCardStyles;
    pagination?: PaginationPresentationProps;
    // XNG-Changes
    customerCardsWrapper?: InjectableCss;
}

const linkTextStyles: TypographyPresentationProps = {
    color: "text.main",
};

export const customerSelectorStyles: CustomerSelectorStyles = {
    customerCardSelected: {
        address: {
            attentionText: linkTextStyles,
            customerNameText: linkTextStyles,
            companyNameText: linkTextStyles,
            address1Text: linkTextStyles,
            address2Text: linkTextStyles,
            address3Text: linkTextStyles,
            address4Text: linkTextStyles,
            cityStatePostalCodeText: linkTextStyles,
            countryText: linkTextStyles,
            phoneText: linkTextStyles,
            emailText: linkTextStyles,
            faxText: linkTextStyles,
        },
    },
};

const CustomerSelector = ({
    customers,
    pagination,
    extendedStyles,
    selectedCustomer,
    isOverlay,
    onChangePage,
    onChangeResultsPerPage,
    onSelect,
    onEdit,
}: OwnProps) => {
    const [styles] = useState(() => mergeToNew(customerSelectorStyles, extendedStyles));
    // XNG-Changes
    const addressesWrapperRef = React.createRef<HTMLDivElement>();

    return (
        <>
            {/* XNG-Changes: add fancy scrol */}
            <FancyScroll refElement={addressesWrapperRef}>
                <StyledWrapper ref={addressesWrapperRef} {...styles.customerCardsWrapper}>
                    {customers.map(customer => {
                        const isCardSelected = customer.id === selectedCustomer?.id;
                        return (
                            <CustomerCard
                                key={customer.id}
                                customer={customer}
                                isSelected={isCardSelected}
                                extendedStyles={styles.customerCard}
                                onSelect={onSelect}
                                onEdit={onEdit}
                                isOverlay={isOverlay}
                            />
                        );
                    })}
                </StyledWrapper>
            </FancyScroll>
            {customers.length > 0 && pagination && (
                <Pagination
                    {...styles.pagination}
                    currentPage={pagination.currentPage}
                    resultsPerPage={pagination.pageSize}
                    resultsCount={pagination.totalItemCount}
                    resultsPerPageOptions={pagination.pageSizeOptions}
                    onChangePage={onChangePage}
                    onChangeResultsPerPage={onChangeResultsPerPage}
                    prevPage={pagination.prevPageUri}
                    nextPage={pagination.nextPageUri}
                    {...{ xBreakPoint: false }}
                />
            )}
        </>
    );
};

export default CustomerSelector;
